import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Card, CardHeader, CardContent, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SupplierManagement() {
  const { user } = useContext(AuthContext);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierContracts, setSupplierContracts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    description: ''
  });

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/suppliers`);
      setSuppliers(response.data);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      toast.error('Erreur lors du chargement des fournisseurs');
    } finally {
      setLoading(false);
    }
  };

  const fetchSupplierContracts = async (supplierId) => {
    try {
      const response = await axios.get(`${API}/suppliers/${supplierId}/contracts`);
      setSupplierContracts(response.data);
    } catch (error) {
      console.error('Error fetching supplier contracts:', error);
      toast.error('Erreur lors du chargement des contrats');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingSupplier) {
        await axios.put(`${API}/suppliers/${editingSupplier.id}`, formData);
        toast.success('Fournisseur mis à jour avec succès');
      } else {
        await axios.post(`${API}/suppliers`, formData);
        toast.success('Fournisseur créé avec succès');
      }
      
      resetForm();
      fetchSuppliers();
    } catch (error) {
      console.error('Error saving supplier:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const deleteSupplier = async (supplierId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce fournisseur ?')) return;

    try {
      await axios.delete(`${API}/suppliers/${supplierId}`);
      setSuppliers(suppliers.filter(s => s.id !== supplierId));
      toast.success('Fournisseur supprimé avec succès');
    } catch (error) {
      console.error('Error deleting supplier:', error);
      if (error.response?.status === 400) {
        toast.error('Impossible de supprimer un fournisseur avec des contrats actifs');
      } else {
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      contact_person: '',
      email: '',
      phone: '',
      address: '',
      description: ''
    });
    setShowAddForm(false);
    setEditingSupplier(null);
  };

  const startEdit = (supplier) => {
    setFormData({
      name: supplier.name,
      contact_person: supplier.contact_person || '',
      email: supplier.email || '',
      phone: supplier.phone || '',
      address: supplier.address || '',
      description: supplier.description || ''
    });
    setEditingSupplier(supplier);
    setShowAddForm(true);
  };

  const viewSupplierDetails = (supplier) => {
    setSelectedSupplier(supplier);
    fetchSupplierContracts(supplier.id);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const filteredSuppliers = suppliers.filter(supplier =>
    supplier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (supplier.contact_person && supplier.contact_person.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (supplier.email && supplier.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading && suppliers.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="supplier-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary">Gestion des Fournisseurs</h1>
          <p className="text-text-muted mt-1">
            {filteredSuppliers.length} fournisseur(s) trouvé(s)
            <span className="font-mono text-xs ml-2 px-2 py-1 bg-bg-muted rounded">
              Total: {suppliers.length} suppliers
            </span>
          </p>
        </div>
        <Button 
          onClick={() => setShowAddForm(true)} 
          className="btn-primary" 
          data-testid="add-supplier-button"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Nouveau fournisseur
        </Button>
      </div>

      {/* Search */}
      <Card className="border-l-4 border-l-primary">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <span className="text-sm font-medium text-text-primary">Rechercher:</span>
            </div>
            
            <div className="flex-1">
              <Input
                placeholder="Nom, contact, email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="search-suppliers-input"
                className="form-input h-8"
              />
            </div>
            
            {searchTerm && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSearchTerm('')}
                className="btn-ghost h-8 px-3 text-xs"
              >
                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Form */}
      {showAddForm && (
        <Card>
          <CardHeader>
            <CardTitle>
              {editingSupplier ? 'Modifier le fournisseur' : 'Nouveau fournisseur'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name" className="form-label">Nom du fournisseur *</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Orange Business Services"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    className="form-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="contact_person" className="form-label">Personne de contact</Label>
                  <Input
                    id="contact_person"
                    type="text"
                    placeholder="Jean Dupont"
                    value={formData.contact_person}
                    onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                    className="form-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email" className="form-label">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="contact@fournisseur.com"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="form-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="phone" className="form-label">Téléphone</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="01 23 45 67 89"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="form-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="address" className="form-label">Adresse</Label>
                <Input
                  id="address"
                  type="text"
                  placeholder="123 Rue de la République, 75001 Paris"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  className="form-input"
                />
              </div>

              <div>
                <Label htmlFor="description" className="form-label">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Notes sur le fournisseur..."
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="form-textarea"
                />
              </div>

              <div className="flex items-center justify-end space-x-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={resetForm}
                  className="btn-outline"
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="btn-primary"
                >
                  {loading ? (
                    <>
                      <div className="loading-spinner mr-2"></div>
                      {editingSupplier ? 'Modification...' : 'Création...'}
                    </>
                  ) : (
                    editingSupplier ? 'Modifier' : 'Créer'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Supplier Details Modal */}
      {selectedSupplier && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h3M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                Détails : {selectedSupplier.name}
              </CardTitle>
              <Button
                variant="ghost"
                onClick={() => setSelectedSupplier(null)}
                className="btn-ghost"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Supplier Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="font-semibold text-text-primary">Informations</h3>
                <div className="space-y-2 text-sm">
                  <div><span className="text-text-muted">Contact:</span> {selectedSupplier.contact_person || 'Non renseigné'}</div>
                  <div><span className="text-text-muted">Email:</span> {selectedSupplier.email || 'Non renseigné'}</div>
                  <div><span className="text-text-muted">Téléphone:</span> {selectedSupplier.phone || 'Non renseigné'}</div>
                  <div><span className="text-text-muted">Adresse:</span> {selectedSupplier.address || 'Non renseignée'}</div>
                </div>
              </div>
              <div className="space-y-4">
                <h3 className="font-semibold text-text-primary">Description</h3>
                <p className="text-sm text-text-muted">
                  {selectedSupplier.description || 'Aucune description'}
                </p>
              </div>
            </div>

            {/* Contracts */}
            <div>
              <h3 className="font-semibold text-text-primary mb-4">
                Contrats associés ({supplierContracts.length})
              </h3>
              {supplierContracts.length > 0 ? (
                <div className="space-y-3">
                  {supplierContracts.map((contract) => (
                    <div key={contract.id} className="p-3 bg-bg-subtle rounded border-l-3 border-l-primary">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-text-primary">{contract.name}</div>
                          <div className="text-sm text-text-muted">
                            {contract.location && `📍 ${contract.location} • `}
                            Échéance: {formatDate(contract.end_date)}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-semibold text-primary">{formatCurrency(contract.amount)}</div>
                          <div className="text-sm text-text-muted">{contract.type_contract}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-text-muted">
                  <svg className="w-12 h-12 mx-auto mb-2 text-text-subtle" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p>Aucun contrat pour ce fournisseur</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Suppliers Table */}
      {!selectedSupplier && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h3M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              Base de données fournisseurs
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {filteredSuppliers.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="table" data-testid="suppliers-table">
                  <thead>
                    <tr>
                      <th>Fournisseur</th>
                      <th>Contact</th>
                      <th>Email</th>
                      <th>Téléphone</th>
                      <th>Statut</th>
                      <th>Créé le</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSuppliers.map((supplier, index) => (
                      <tr key={supplier.id} data-testid={`supplier-row-${supplier.id}`} className="group">
                        <td>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center text-white text-sm font-semibold">
                              {supplier.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div className="font-semibold text-text-primary cursor-pointer hover:text-primary"
                                   onClick={() => viewSupplierDetails(supplier)}>
                                {supplier.name}
                              </div>
                              <div className="text-xs text-text-muted font-mono">
                                ID: {supplier.id.slice(-8)}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="text-text-secondary">{supplier.contact_person || '-'}</td>
                        <td className="text-text-secondary font-mono text-sm">{supplier.email || '-'}</td>
                        <td className="text-text-secondary">{supplier.phone || '-'}</td>
                        <td>
                          <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                            supplier.is_active ? 'bg-green-100 text-success' : 'bg-red-100 text-error'
                          }`}>
                            <span className={`w-1.5 h-1.5 rounded-full mr-1 ${
                              supplier.is_active ? 'bg-success' : 'bg-error'
                            }`}></span>
                            {supplier.is_active ? 'Actif' : 'Inactif'}
                          </span>
                        </td>
                        <td className="text-text-secondary text-sm">
                          {formatDate(supplier.created_at)}
                        </td>
                        <td className="text-right">
                          <div className="flex items-center justify-end space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => viewSupplierDetails(supplier)}
                              className="btn-ghost text-primary hover:text-primary/80 p-2"
                              title="Voir détails"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => startEdit(supplier)}
                              data-testid={`edit-supplier-${supplier.id}`}
                              className="btn-ghost text-primary hover:text-primary/80 p-2"
                              title="Modifier"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteSupplier(supplier.id)}
                              data-testid={`delete-supplier-${supplier.id}`}
                              className="btn-ghost text-error hover:text-error/80 p-2"
                              title="Supprimer"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <svg className="w-16 h-16 mx-auto mb-4 text-text-subtle" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h3M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <h3 className="text-lg font-semibold text-text-primary mb-2">Aucun fournisseur trouvé</h3>
                <p className="text-text-muted mb-4">
                  {suppliers.length === 0 
                    ? "Commencez par créer votre premier fournisseur" 
                    : "Aucun résultat pour cette recherche"
                  }
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}